import {
  useVaultInvitations,
  useCreateInvitation,
  useRevokeInvitation,
  useResendInvitation,
} from '@/hooks/useVault';
import { InvitationStatus, PermissionRole } from '@/types/vault.types';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Clock,
  CheckCircle,
  Mail,
  MailPlus,
  Plus,
  RefreshCw,
  Trash2,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

const createInvitationSchema = z.object({
  email: z.string().email('vault.invitations.validation.email'),
  permissionRole: z.nativeEnum(PermissionRole),
  message: z.string().max(1000).optional(),
});

type CreateInvitationFormData = z.infer<typeof createInvitationSchema>;

interface InvitationsTabProps {
  vaultId: string;
}

export const InvitationsTab = ({ vaultId }: InvitationsTabProps) => {
  const { t } = useTranslation();
  const [showInviteModal, setShowInviteModal] = useState(false);

  const { data: invitations, isLoading } = useVaultInvitations(vaultId);
  const revokeInvitation = useRevokeInvitation(vaultId);
  const resendInvitation = useResendInvitation(vaultId);

  const handleRevoke = (invitationId: string) => {
    if (
      window.confirm(
        t(
          'vault.invitations.revokeConfirm',
          'Êtes-vous sûr de vouloir révoquer cette invitation ?',
        ),
      )
    ) {
      revokeInvitation.mutate(invitationId);
    }
  };

  const handleResend = (invitationId: string) => {
    resendInvitation.mutate({ invitationId });
  };

  const getStatusBadge = (status: InvitationStatus) => {
    switch (status) {
      case InvitationStatus.PENDING:
        return {
          icon: <Clock className="h-3 w-3" />,
          className: 'bg-amber-100 text-amber-700 border-amber-200',
          label: t('vault.invitations.status.pending', 'En attente'),
        };
      case InvitationStatus.ACCEPTED:
        return {
          icon: <CheckCircle className="h-3 w-3" />,
          className: 'bg-success-100 text-success-700 border-success-200',
          label: t('vault.invitations.status.accepted', 'Acceptée'),
        };
      case InvitationStatus.EXPIRED:
        return {
          icon: <XCircle className="h-3 w-3" />,
          className: 'bg-neutral-100 text-neutral-700 border-neutral-200',
          label: t('vault.invitations.status.expired', 'Expirée'),
        };
      case InvitationStatus.REVOKED:
        return {
          icon: <XCircle className="h-3 w-3" />,
          className: 'bg-error-100 text-error-700 border-red-200',
          label: t('vault.invitations.status.revoked', 'Révoquée'),
        };
      default:
        return {
          icon: null,
          className: 'bg-neutral-100 text-neutral-700 border-neutral-200',
          label: status,
        };
    }
  };

  if (isLoading) {
    return (
      <div className="rounded-lg border border-neutral-200 bg-white p-6">
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-50">
            <Mail className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">
              {t('vault.invitations.title', 'Invitations')}
            </h2>
            <p className="text-sm text-neutral-500">
              {t(
                'vault.invitations.description',
                'Invitez des utilisateurs à accéder au coffre',
              )}
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700"
        >
          <MailPlus className="h-4 w-4" />
          {t('vault.invitations.invite', 'Inviter un utilisateur')}
        </button>
      </div>

      {/* Invitations List */}
      {invitations && invitations.length > 0 ? (
        <div className="overflow-hidden rounded-lg border border-neutral-200">
          <table className="w-full">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-neutral-500">
                  {t('vault.invitations.email', 'Email')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-neutral-500">
                  {t('vault.invitations.role', 'Rôle')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-neutral-500">
                  {t('vault.invitations.status', 'Statut')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-neutral-500">
                  {t('vault.invitations.sentAt', 'Envoyée le')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-neutral-500">
                  {t('vault.invitations.expiresAt', 'Expire le')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-neutral-500">
                  {t('common.actions', 'Actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200 bg-white">
              {invitations.map((invitation) => {
                const statusBadge = getStatusBadge(invitation.status);
                return (
                  <tr key={invitation.uuid} className="hover:bg-neutral-50">
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-100 text-sm font-medium text-purple-600">
                          {invitation.email.charAt(0).toUpperCase()}
                        </div>
                        <div className="text-sm font-medium text-neutral-900">
                          {invitation.email}
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <span className="rounded-full border border-neutral-200 bg-neutral-100 px-3 py-1 text-xs font-medium text-neutral-700">
                        {invitation.permissionRole}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium ${statusBadge.className}`}
                      >
                        {statusBadge.icon}
                        {statusBadge.label}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-neutral-500">
                      {new Date(invitation.createdAt).toLocaleDateString(
                        'fr-FR',
                      )}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-neutral-500">
                      {new Date(invitation.expiresAt).toLocaleDateString(
                        'fr-FR',
                      )}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {invitation.status === InvitationStatus.PENDING && (
                          <>
                            <button
                              onClick={() => handleResend(invitation.uuid)}
                              className="flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-100"
                            >
                              <RefreshCw className="h-3 w-3" />
                              {t('vault.invitations.resend', 'Renvoyer')}
                            </button>
                            <button
                              onClick={() => handleRevoke(invitation.uuid)}
                              className="flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-3 py-1 text-sm font-medium text-red-700 transition-colors hover:bg-red-100"
                            >
                              <Trash2 className="h-3 w-3" />
                              {t('vault.invitations.revoke', 'Révoquer')}
                            </button>
                          </>
                        )}
                        {invitation.status === InvitationStatus.EXPIRED && (
                          <button
                            onClick={() => handleResend(invitation.uuid)}
                            className="flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-100"
                          >
                            <RefreshCw className="h-3 w-3" />
                            {t('vault.invitations.renew', 'Renouveler')}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-neutral-300 bg-neutral-50 p-12 text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-neutral-200">
            <Mail className="h-8 w-8 text-neutral-500" />
          </div>
          <h3 className="mb-2 text-lg font-semibold text-neutral-900">
            {t('vault.invitations.empty', 'Aucune invitation')}
          </h3>
          <p className="mb-4 text-sm text-neutral-600">
            {t(
              'vault.invitations.emptyDescription',
              'Invitez des utilisateurs à rejoindre ce coffre',
            )}
          </p>
          <button
            onClick={() => setShowInviteModal(true)}
            className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700"
          >
            <Plus className="h-4 w-4" />
            {t('vault.invitations.sendFirst', 'Envoyer la première invitation')}
          </button>
        </div>
      )}

      {/* Invite User Modal */}
      {showInviteModal && (
        <InviteUserModal
          vaultId={vaultId}
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
        />
      )}
    </div>
  );
};

// Invite User Modal Component
interface InviteUserModalProps {
  vaultId: string;
  isOpen: boolean;
  onClose: () => void;
}

const InviteUserModal = ({
  vaultId,
  isOpen,
  onClose,
}: InviteUserModalProps) => {
  const { t } = useTranslation();
  const createInvitation = useCreateInvitation(vaultId);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<CreateInvitationFormData>({
    resolver: zodResolver(createInvitationSchema),
    defaultValues: {
      permissionRole: PermissionRole.VIEWER,
    },
  });

  const onSubmit = async (data: CreateInvitationFormData) => {
    try {
      await createInvitation.mutateAsync(data);
      reset();
      onClose();
    } catch {
      // Error handled by hook
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="relative w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-purple-100">
            <MailPlus className="h-8 w-8 text-purple-600" />
          </div>
          <h2 className="text-2xl font-bold text-neutral-900">
            {t('vault.invitations.inviteTitle', 'Inviter un Utilisateur')}
          </h2>
          <p className="mt-2 text-sm text-neutral-600">
            {t(
              'vault.invitations.inviteDescription',
              'Envoyez une invitation par email pour accéder au coffre',
            )}
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Email */}
          <div>
            <label className="mb-2 block text-sm font-medium text-neutral-700">
              {t('vault.invitations.emailLabel', 'Adresse Email')}
              <span className="text-error-600">*</span>
            </label>
            <input
              {...register('email')}
              type="email"
              className="block w-full rounded-lg border border-neutral-300 px-3 py-2 shadow-sm transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
              placeholder="utilisateur@exemple.com"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-error-600">
                {t(errors.email.message as string)}
              </p>
            )}
          </div>

          {/* Role */}
          <div>
            <label className="mb-2 block text-sm font-medium text-neutral-700">
              {t('vault.invitations.roleLabel', 'Rôle')}
              <span className="text-error-600">*</span>
            </label>
            <select
              {...register('permissionRole')}
              className="block w-full rounded-lg border border-neutral-300 px-3 py-2 shadow-sm transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
            >
              <option value={PermissionRole.VIEWER}>
                {PermissionRole.VIEWER} - Lecture seule
              </option>
              <option value={PermissionRole.CONTRIBUTOR}>
                {PermissionRole.CONTRIBUTOR} - Contributeur
              </option>
              <option value={PermissionRole.CREATOR}>
                {PermissionRole.CREATOR} - Créateur (historique + config sync)
              </option>
              <option value={PermissionRole.ADMIN}>
                {PermissionRole.ADMIN} - Administrateur
              </option>
            </select>
          </div>

          {/* Optional Message */}
          <div>
            <label className="mb-2 block text-sm font-medium text-neutral-700">
              {t('vault.invitations.messageLabel', 'Message (optionnel)')}
            </label>
            <textarea
              {...register('message')}
              rows={3}
              className="block w-full rounded-lg border border-neutral-300 px-3 py-2 shadow-sm transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
              placeholder={t('vault.invitations.messagePlaceholder', 'Ajoutez un message personnalisé...')}
            />
          </div>

          {/* Info */}
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
            <p className="text-sm text-blue-800">
              {t(
                'vault.invitations.info',
                "L'utilisateur recevra un email avec un lien d'invitation valide pendant 7 jours.",
              )}
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
            >
              {t('common.cancel', 'Annuler')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
            >
              <Mail className="h-4 w-4" />
              {isSubmitting
                ? t('common.sending', 'Envoi...')
                : t('vault.invitations.send', 'Envoyer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
